import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaCloudUploadAlt, FaFolderPlus, FaList, FaShareAlt, FaSyncAlt, FaThLarge, FaTrashAlt, FaUpload } from 'react-icons/fa'
import { MdPlaylistAdd } from 'react-icons/md'
import Swal from 'sweetalert2'
import MainLayout from '../../components/Layout'
import Button from '../../components/atoms/Buttons'
import { createToast } from '../../utils/Toast'
import { useDownloadQueue } from '../../context/DownloadQueueContext'
import { useUserContext } from '../../context/UserContext'
import useS3FileBrowser from '../../hooks/useS3FileBrowser'
import useMyFileGrants from '../../hooks/useMyFileGrants'
import useThumbnails from '../../hooks/useThumbnails'
import { createFolder, deleteFiles, downloadFromUrl, getBatchUrls, getUploadUrl, uploadFileToUrl } from '../../utils/fileBrowserApi'
import FolderTree from './components/FolderTree'
import FileList from './components/FileList'
import FileGrid from './components/FileGrid'
import NewFolderModal from './components/NewFolderModal'
import FilePreviewModal from './components/FilePreviewModal'
import ManageAccessModal from './components/ManageAccessModal'
import ShareByEmailModal from '../../components/DownloadQueue/ShareByEmailModal'
import { collectDroppedEntries } from './dropUtils'

const Toast = createToast()

const BUCKETS = [
  { id: 'private', label: 'Private' },
  { id: 'public', label: 'Public' },
]

function Breadcrumb({ bucket, prefix, onNavigate }) {
  const segments = prefix.split('/').filter(Boolean)
  return (
    <nav className="flex items-center flex-wrap gap-1 text-sm font-montserrat text-gray-500">
      <span
        className="cursor-pointer hover:text-[#2b364b] hover:underline capitalize"
        onClick={() => onNavigate('')}
      >
        {bucket} bucket
      </span>
      {segments.map((segment, index) => {
        const target = segments.slice(0, index + 1).join('/') + '/'
        const isLast = index === segments.length - 1
        return (
          <span key={target} className="flex items-center gap-1">
            <span>/</span>
            <span
              className={
                isLast
                  ? 'text-[#2b364b] font-semibold'
                  : 'cursor-pointer hover:text-[#2b364b] hover:underline'
              }
              onClick={() => !isLast && onNavigate(target)}
            >
              {segment}
            </span>
          </span>
        )
      })}
    </nav>
  )
}

export default function FileBrowser() {
  const {
    bucket,
    prefix,
    folders,
    files,
    loading,
    loadingMore,
    error,
    hasMore,
    navigateTo,
    switchBucket,
    refresh,
    loadMore,
  } = useS3FileBrowser('private')

  const { addItems, isQueued, removeItem } = useDownloadQueue()
  const { isSaddlefitAdmin } = useUserContext()
  const { accessByPrefix } = useMyFileGrants()
  const location = useLocation()

  // Super admins manage everything (public bucket, Manage Access). Other
  // accounts are locked to the private bucket and get write actions
  // (upload/new-folder/delete/share) only inside folders they hold a write
  // grant for — the backend re-checks every call.
  const canManage = isSaddlefitAdmin
  const canWriteHere =
    canManage ||
    (bucket === 'private' &&
      Object.entries(accessByPrefix).some(
        ([p, access]) => access === 'write' && prefix.startsWith(p)
      ))

  const [selectedKeys, setSelectedKeys] = useState(new Set())
  const [manageAccessFolder, setManageAccessFolder] = useState(null)

  // Deep link from the dashboard "Private" card.
  useEffect(() => {
    const target = location.state?.prefix
    if (target) navigateTo(target)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.prefix])
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [viewingKey, setViewingKey] = useState(null)
  const [preview, setPreview] = useState(null)
  const [shareItems, setShareItems] = useState(null)
  const [treeVersion, setTreeVersion] = useState(0)
  const [navCollapsed, setNavCollapsed] = useState(false)
  const [viewMode, setViewMode] = useState(() =>
    localStorage.getItem('fileBrowserViewMode') === 'grid' ? 'grid' : 'list'
  )
  const [tileSize, setTileSize] = useState(() => {
    const saved = localStorage.getItem('fileBrowserTileSize')
    return ['sm', 'md', 'lg'].includes(saved) ? saved : 'md'
  })
  const [isDragging, setIsDragging] = useState(false)
  const [uploadStatus, setUploadStatus] = useState(null)
  const dragCounter = useRef(0)
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)

  const changeViewMode = mode => {
    setViewMode(mode)
    try {
      localStorage.setItem('fileBrowserViewMode', mode)
    } catch {
      /* storage unavailable */
    }
  }

  const changeTileSize = size => {
    setTileSize(size)
    try {
      localStorage.setItem('fileBrowserTileSize', size)
    } catch {
      /* storage unavailable */
    }
  }

  // Hook stays mounted in list view so the URL cache survives toggling;
  // `enabled` gates the actual fetching.
  const thumbs = useThumbnails({ bucket, files, enabled: viewMode === 'grid' })

  const changeLocation = fn => {
    setSelectedKeys(new Set())
    selectionAnchor.current = null
    fn()
  }

  // Last file clicked without shift — the start point for shift+click ranges.
  const selectionAnchor = useRef(null)

  const toggleSelect = (file, shiftKey = false) => {
    if (shiftKey && selectionAnchor.current) {
      const keys = files.map(f => f.key)
      const anchorIndex = keys.indexOf(selectionAnchor.current)
      const targetIndex = keys.indexOf(file.key)
      if (anchorIndex !== -1 && targetIndex !== -1) {
        const [start, end] =
          anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
        setSelectedKeys(prev => {
          const next = new Set(prev)
          for (let i = start; i <= end; i++) next.add(keys[i])
          return next
        })
        return
      }
    }
    selectionAnchor.current = file.key
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedKeys(prev =>
      prev.size === files.length ? new Set() : new Set(files.map(f => f.key)),
    )
  }

  const addToQueue = items => {
    addItems(items.map(f => ({ ...f, bucket })))
    Toast.fire({
      icon: 'success',
      title: `${items.length > 1 ? `${items.length} files` : items[0].name} added to download queue`,
    })
  }

  const addSelectedToQueue = () => {
    const selected = files.filter(f => selectedKeys.has(f.key))
    if (!selected.length) return
    addToQueue(selected)
    setSelectedKeys(new Set())
  }

  const shareSelected = () => {
    const selected = files.filter(f => selectedKeys.has(f.key))
    if (!selected.length) return
    setShareItems(selected.map(f => ({ ...f, bucket })))
  }

  const confirmDelete = async text => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#2b364b',
    })
    return result.isConfirmed
  }

  const handleDeleteFile = async file => {
    if (file.readOnly) {
      Toast.fire({ icon: 'info', title: `"${file.name}" is read-only` })
      return
    }
    if (!(await confirmDelete(`Delete "${file.name}"? This cannot be undone.`))) return
    try {
      const res = await deleteFiles([{ bucket, key: file.key }])
      if (res.errors?.length) throw new Error(res.errors[0].message)
      removeItem({ bucket, key: file.key })
      setSelectedKeys(prev => {
        const next = new Set(prev)
        next.delete(file.key)
        return next
      })
      Toast.fire({ icon: 'success', title: `"${file.name}" deleted` })
      refresh()
    } catch (err) {
      console.error('Delete failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to delete file' })
    }
  }

  const handleDeleteFolder = async folder => {
    if (
      !(await confirmDelete(
        `Delete folder "${folder.name}"? Only empty folders can be deleted.`,
      ))
    )
      return
    try {
      const res = await deleteFiles([{ bucket, key: folder.prefix }])
      if (res.errors?.length) throw new Error(res.errors[0].message)
      Toast.fire({ icon: 'success', title: `Folder "${folder.name}" deleted` })
      setTreeVersion(v => v + 1)
      refresh()
    } catch (err) {
      console.error('Delete folder failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to delete folder' })
    }
  }

  const deleteSelected = async () => {
    const selected = files.filter(f => selectedKeys.has(f.key))
    if (!selected.length) return
    const deletable = selected.filter(f => !f.readOnly)
    const skipped = selected.length - deletable.length
    if (!deletable.length) {
      Toast.fire({ icon: 'info', title: 'Selected files are read-only' })
      return
    }
    if (
      !(await confirmDelete(
        `Delete ${deletable.length} file${deletable.length > 1 ? 's' : ''}? This cannot be undone.${
          skipped ? ` ${skipped} read-only file${skipped > 1 ? 's' : ''} will be skipped.` : ''
        }`,
      ))
    )
      return
    try {
      const res = await deleteFiles(deletable.map(f => ({ bucket, key: f.key })))
      for (const d of res.deleted || []) removeItem(d)
      setSelectedKeys(new Set())
      if (res.errors?.length) {
        Toast.fire({
          icon: 'warning',
          title: `${res.deleted?.length || 0} deleted, ${res.errors.length} failed`,
        })
      } else {
        Toast.fire({
          icon: 'success',
          title: `${res.deleted?.length || 0} file${(res.deleted?.length || 0) > 1 ? 's' : ''} deleted`,
        })
      }
      refresh()
    } catch (err) {
      console.error('Bulk delete failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to delete files' })
    }
  }

  // Explorer-style shortcuts: Ctrl/Cmd+A select all, Escape clear, Delete
  // deletes the selection. Suppressed while typing or while any modal is open.
  useEffect(() => {
    const onKeyDown = event => {
      const target = event.target
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      )
        return
      if (Swal.isVisible() || preview || shareItems || showNewFolder || manageAccessFolder) return
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
        if (!files.length) return
        event.preventDefault()
        setSelectedKeys(new Set(files.map(f => f.key)))
      } else if (event.key === 'Escape') {
        setSelectedKeys(new Set())
        selectionAnchor.current = null
      } else if (event.key === 'Delete' && canWriteHere && selectedKeys.size) {
        deleteSelected()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })

  const handleDownload = async file => {
    setDownloadingKey(file.key)
    try {
      const [{ url }] = await getBatchUrls([{ bucket, key: file.key }])
      await downloadFromUrl(url, file.name)
    } catch (err) {
      console.error('Download failed:', err)
      Toast.fire({ icon: 'error', title: 'Failed to download file' })
    } finally {
      setDownloadingKey(null)
    }
  }

  const handleView = async file => {
    setViewingKey(file.key)
    try {
      const [{ url }] = await getBatchUrls([{ bucket, key: file.key }])
      setPreview({ file, url })
    } catch (err) {
      console.error('View failed:', err)
      Toast.fire({ icon: 'error', title: 'Failed to open file' })
    } finally {
      setViewingKey(null)
    }
  }

  // entries: [{ file, relPath }] where relPath is the path within the current
  // folder (e.g. "myFolder/sub/photo.jpg"); plain uploads use just file.name.
  const uploadFiles = async entries => {
    const all = entries.filter(e => e.file && e.relPath)
    // The backend rejects file names starting with a dot (.DS_Store etc).
    const toUpload = all.filter(e => !e.relPath.split('/').pop().startsWith('.'))
    const skipped = all.length - toUpload.length
    if (!toUpload.length || uploadStatus) return

    const failed = []
    let hasSubfolders = false
    for (let i = 0; i < toUpload.length; i++) {
      const { file, relPath } = toUpload[i]
      setUploadStatus({ done: i, total: toUpload.length, name: relPath })
      const lastSlash = relPath.lastIndexOf('/')
      const dir = lastSlash === -1 ? '' : relPath.slice(0, lastSlash)
      const fileName = lastSlash === -1 ? relPath : relPath.slice(lastSlash + 1)
      try {
        const { signedUrl } = await getUploadUrl({
          bucket,
          prefix: dir ? `${prefix}${dir}/` : prefix,
          fileName,
          contentType: file.type || 'application/octet-stream',
        })
        await uploadFileToUrl(file, signedUrl)
        if (dir) hasSubfolders = true
      } catch (err) {
        console.error(`Upload failed for ${relPath}:`, err)
        failed.push(relPath)
      }
    }
    setUploadStatus(null)
    refresh()
    if (hasSubfolders) setTreeVersion(v => v + 1)

    const okCount = toUpload.length - failed.length
    const skippedNote = skipped ? `, ${skipped} hidden file${skipped > 1 ? 's' : ''} skipped` : ''
    if (failed.length) {
      Toast.fire({
        icon: okCount ? 'warning' : 'error',
        title: `${okCount} uploaded, ${failed.length} failed: ${failed.join(', ')}${skippedNote}`,
      })
    } else {
      Toast.fire({
        icon: 'success',
        title: `${okCount} file${okCount > 1 ? 's' : ''} uploaded${skippedNote}`,
      })
    }
  }

  const filesToEntries = fileList =>
    Array.from(fileList).map(file => ({
      file,
      relPath: file.webkitRelativePath || file.name,
    }))

  const handleDragEnter = event => {
    event.preventDefault()
    dragCounter.current += 1
    if (event.dataTransfer?.types?.includes('Files')) setIsDragging(true)
  }

  const handleDragLeave = event => {
    event.preventDefault()
    dragCounter.current -= 1
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragging(false)
    }
  }

  const handleDrop = async event => {
    event.preventDefault()
    dragCounter.current = 0
    setIsDragging(false)
    const entries = await collectDroppedEntries(event.dataTransfer)
    if (entries.length) uploadFiles(entries)
  }

  const handleCreateFolder = async folderName => {
    setCreatingFolder(true)
    try {
      await createFolder({ bucket, prefix, folderName })
      Toast.fire({ icon: 'success', title: `Folder "${folderName}" created` })
      setShowNewFolder(false)
      setTreeVersion(v => v + 1)
      refresh()
    } catch (err) {
      console.error('Create folder failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to create folder' })
    } finally {
      setCreatingFolder(false)
    }
  }

  const listProps = {
    loading,
    folders,
    files,
    selectedKeys,
    onToggleSelect: toggleSelect,
    onToggleSelectAll: toggleSelectAll,
    onOpenFolder: p => changeLocation(() => navigateTo(p)),
    onView: handleView,
    onDownload: handleDownload,
    onShare:
      canManage || bucket === 'private'
        ? file => setShareItems([{ ...file, bucket }])
        : undefined,
    onAddToQueue: file => addToQueue([file]),
    onDeleteFile: canWriteHere ? handleDeleteFile : undefined,
    onDeleteFolder: canWriteHere ? handleDeleteFolder : undefined,
    onManageAccess:
      canManage && bucket === 'private'
        ? folder => setManageAccessFolder(folder)
        : undefined,
    isQueued,
    downloadingKey,
    viewingKey,
  }

  return (
    <MainLayout>
      <div className="w-full flex items-center flex-wrap justify-between mb-5 sm:mb-0 h-auto">
        <h1 className="admin-dashboard">{canManage ? 'File Browser' : 'Private Files'}</h1>
        {canWriteHere && (
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={event => {
                uploadFiles(filesToEntries(event.target.files))
                event.target.value = ''
              }}
            />
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory=""
              multiple
              className="hidden"
              onChange={event => {
                uploadFiles(filesToEntries(event.target.files))
                event.target.value = ''
              }}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              AppendIcon={FaUpload}
              disabled={Boolean(uploadStatus)}
              className="create-saddle-btn !w-auto px-8 cursor-pointer"
            >
              Upload Files
            </Button>
            <Button
              onClick={() => folderInputRef.current?.click()}
              AppendIcon={FaCloudUploadAlt}
              disabled={Boolean(uploadStatus)}
              className="create-saddle-btn !w-auto px-8 cursor-pointer"
            >
              Upload Folder
            </Button>
            <Button
              onClick={() => setShowNewFolder(true)}
              AppendIcon={FaFolderPlus}
              className="create-saddle-btn !w-auto px-8 cursor-pointer"
            >
              New Folder
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mt-4 font-montserrat">
        {/* Left panel — bucket switcher + folder tree (collapsible) */}
        {navCollapsed ? (
          <div className="hidden lg:block shrink-0">
            <button
              type="button"
              title="Expand navigation"
              onClick={() => setNavCollapsed(false)}
              className="border border-gray-300 rounded-lg bg-white text-gray-500 hover:text-[#2b364b] hover:bg-gray-50 cursor-pointer p-2"
            >
              <FaAngleDoubleRight size={15} />
            </button>
          </div>
        ) : (
          <div className="w-full lg:w-[260px] shrink-0">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex flex-1 rounded-lg overflow-hidden border border-gray-300">
                {(canManage ? BUCKETS : BUCKETS.filter(b => b.id === 'private')).map(b => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => canManage && changeLocation(() => switchBucket(b.id))}
                    className={`flex-1 py-2 text-sm font-medium border-none ${
                      bucket === b.id
                        ? 'bg-[#2b364b] text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    } ${canManage ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                title="Collapse navigation"
                onClick={() => setNavCollapsed(true)}
                className="hidden lg:block border border-gray-300 rounded-lg bg-white text-gray-500 hover:text-[#2b364b] hover:bg-gray-50 cursor-pointer p-2"
              >
                <FaAngleDoubleLeft size={15} />
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg p-2 max-h-[60vh] overflow-y-auto custom-scrollbar hidden lg:block">
              <FolderTree
                key={`${bucket}-${treeVersion}`}
                bucket={bucket}
                activePrefix={prefix}
                onNavigate={p => changeLocation(() => navigateTo(p))}
              />
            </div>
          </div>
        )}

        {/* Center — breadcrumb + file list */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <Breadcrumb
              bucket={bucket}
              prefix={prefix}
              onNavigate={p => changeLocation(() => navigateTo(p))}
            />
            <div className="flex items-center gap-3">
              {selectedKeys.size > 0 && (
                <>
                  {(canManage || bucket === 'private') && (
                    <Button
                      onClick={shareSelected}
                      AppendIcon={FaShareAlt}
                      primary
                      className="create-saddle-btn !w-auto px-6 cursor-pointer"
                    >
                      Share {selectedKeys.size}
                    </Button>
                  )}
                  <Button
                    onClick={addSelectedToQueue}
                    AppendIcon={MdPlaylistAdd}
                    primary
                    className="create-saddle-btn !w-auto px-6 cursor-pointer"
                  >
                    Queue {selectedKeys.size}
                  </Button>
                  {canWriteHere && (
                    <Button
                      onClick={deleteSelected}
                      AppendIcon={FaTrashAlt}
                      className="create-saddle-btn !w-auto px-6 cursor-pointer !bg-red-600"
                    >
                      Delete {selectedKeys.size}
                    </Button>
                  )}
                </>
              )}
              {viewMode === 'grid' && (
                <div className="flex rounded-lg overflow-hidden border border-gray-300">
                  {[
                    { id: 'sm', label: 'S', title: 'Small tiles' },
                    { id: 'md', label: 'M', title: 'Medium tiles' },
                    { id: 'lg', label: 'L', title: 'Large tiles' },
                  ].map(size => (
                    <button
                      key={size.id}
                      type="button"
                      title={size.title}
                      onClick={() => changeTileSize(size.id)}
                      className={`px-2.5 py-1.5 text-xs font-semibold border-none cursor-pointer ${
                        tileSize === size.id
                          ? 'bg-[#2b364b] text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex rounded-lg overflow-hidden border border-gray-300">
                <button
                  type="button"
                  title="List view"
                  onClick={() => changeViewMode('list')}
                  className={`p-2 border-none cursor-pointer ${
                    viewMode === 'list'
                      ? 'bg-[#2b364b] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FaList size={13} />
                </button>
                <button
                  type="button"
                  title="Grid view"
                  onClick={() => changeViewMode('grid')}
                  className={`p-2 border-none cursor-pointer ${
                    viewMode === 'grid'
                      ? 'bg-[#2b364b] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FaThLarge size={13} />
                </button>
              </div>
              <button
                type="button"
                title="Refresh"
                onClick={refresh}
                className="text-gray-500 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-1"
              >
                <FaSyncAlt size={15} />
              </button>
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm mb-3">
              {error}
            </p>
          )}

          {uploadStatus && (
            <div className="flex items-center gap-2 mb-3 px-4 py-2 rounded-lg bg-blue-50 text-sm text-[#2b364b]">
              <FaCloudUploadAlt size={16} />
              <span className="truncate">
                Uploading {uploadStatus.done + 1}/{uploadStatus.total}: {uploadStatus.name}
              </span>
            </div>
          )}

          <div
            className="relative"
            onDragEnter={canWriteHere ? handleDragEnter : undefined}
            onDragOver={canWriteHere ? event => event.preventDefault() : undefined}
            onDragLeave={canWriteHere ? handleDragLeave : undefined}
            onDrop={canWriteHere ? handleDrop : undefined}
          >
            {isDragging && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed border-[#2b364b] bg-[#DEE6F0] bg-opacity-90 pointer-events-none min-h-[200px]">
                <FaCloudUploadAlt size={36} className="text-[#2b364b]" />
                <p className="text-sm font-semibold text-[#2b364b] m-0">
                  Drop files to upload to {bucket} bucket / {prefix || 'root'}
                </p>
              </div>
            )}

            {viewMode === 'grid' ? (
              <FileGrid {...listProps} thumbs={thumbs} tileSize={tileSize} />
            ) : (
              <FileList {...listProps} />
            )}
          </div>

          {hasMore && (
            <div className="flex justify-center mt-4">
              <Button
                onClick={loadMore}
                loading={loadingMore}
                primaryOutline
                className="create-saddle-btn !w-auto px-8 cursor-pointer"
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>

      {shareItems && (
        <ShareByEmailModal items={shareItems} onClose={() => setShareItems(null)} />
      )}

      {preview && (
        <FilePreviewModal
          file={preview.file}
          url={preview.url}
          onClose={() => setPreview(null)}
        />
      )}

      {showNewFolder && (
        <NewFolderModal
          currentPath={`${bucket} bucket / ${prefix || ''}`}
          onClose={() => setShowNewFolder(false)}
          onCreate={handleCreateFolder}
          creating={creatingFolder}
        />
      )}

      {manageAccessFolder && (
        <ManageAccessModal
          folder={manageAccessFolder}
          onClose={() => setManageAccessFolder(null)}
        />
      )}
    </MainLayout>
  )
}
